import React, { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Sparkles, CloudRain, Loader2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';

const trafficColor = (t) => (t === 'heavy' ? 'bg-red-100 text-red-700' : t === 'moderate' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700');

// AI route brief card for a delivery (driver / admin). Weather-aware ETA & tips.
export const AiRouteBrief = ({ orderId }) => {
  const [brief, setBrief] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try { const r = await api.get(`/ai/route-brief/${orderId}`); setBrief(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Route brief failed'); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-border bg-card p-4" data-testid="ai-route-brief-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /><span className="text-sm font-semibold">AI Route Brief</span></div>
        <Button size="sm" variant="secondary" onClick={run} disabled={busy} data-testid="ai-route-brief-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : brief ? 'Refresh' : 'Get Brief'}
        </Button>
      </div>
      {busy && <div className="mt-3 space-y-2"><Skeleton className="h-5 w-2/3" /><Skeleton className="h-4 w-full" /><Skeleton className="h-4 w-5/6" /></div>}
      {!busy && brief && (
        <div className="mt-3 space-y-2 text-sm" data-testid="ai-route-brief-result">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline">~{brief.road_km} km</Badge>
            <Badge variant="outline">ETA {brief.eta_min} min</Badge>
            {brief.traffic_level && <Badge className={`${trafficColor(brief.traffic_level)} border-0 capitalize`}>{brief.traffic_level} traffic</Badge>}
          </div>
          {brief.weather_impact && <p className="text-xs text-muted-foreground flex items-start gap-1.5"><CloudRain className="h-3.5 w-3.5 mt-0.5 shrink-0" />{brief.weather_impact}</p>}
          {(brief.route_tips || []).length > 0 && <ul className="text-xs space-y-1 list-disc pl-4">{brief.route_tips.map((t) => <li key={t}>{t}</li>)}</ul>}
          {brief.safety_note && <p className="text-xs flex items-start gap-1.5 text-amber-700"><ShieldAlert className="h-3.5 w-3.5 mt-0.5 shrink-0" />{brief.safety_note}</p>}
        </div>
      )}
    </div>
  );
};
